import {useMutation} from '@apollo/client';
import {message} from 'antd';
import {useTranslation} from 'react-i18next';
import {showError} from '../../../../utils/exceptionUtils';
import {
    SUBMIT_POP_REVIEW_ACTION,
    SubmitPopReviewActionResponse
} from '../graphql/mutations/popReviewMutations';
import {PopReviewAction} from '../graphql/types/PopReviewType';

/**
 * POP 审核提交（一次一单，批量由调用方循环调用）
 *
 * 审核员不由前端传，后端取当前登录账号以防伪造审核人（与 POD 传 reviewerAccountId 不同）。
 */
export const usePopReviewActions = () => {
    const {t} = useTranslation();
    const [submitAction, {loading}] = useMutation<SubmitPopReviewActionResponse>(
        SUBMIT_POP_REVIEW_ACTION
    );

    const handleReview = async (
        reviewTaskId: string,
        action: PopReviewAction,
        disqualifyReasonCodes?: string[],
        reviewRemark?: string,
        reviewRemarkLang?: string
    ): Promise<boolean> => {
        // 判定「疑似虚假」必须给出原因，否则后端会拒绝；此处前置拦截以免无效请求
        if (action === PopReviewAction.REJECT && !disqualifyReasonCodes?.length) {
            message.error(t('popReview.messages.reasonRequired'));
            return false;
        }

        // 「其他不合规情况」必须补充备注说明
        if (disqualifyReasonCodes?.includes('OTHER_NONCOMPLIANCE')
                && !reviewRemark?.trim()) {
            message.error(t('popReview.messages.remarkRequiredForOther'));
            return false;
        }

        try {
            const {data} = await submitAction({
                variables: {
                    reviewTaskId,
                    action,
                    disqualifyReasonCodes: disqualifyReasonCodes?.length ? disqualifyReasonCodes : null,
                    reviewRemark: reviewRemark || null,
                    // 无备注时不必传语种（后端仅在有备注时构造富化 JSON）
                    reviewRemarkLang: reviewRemark ? reviewRemarkLang || null : null
                }
            });

            if (data?.submitPopReviewAction) {
                const actionText =
                    action === PopReviewAction.APPROVE
                        ? t('popReview.messages.approved')
                        : t('popReview.messages.rejected');
                message.success(t('popReview.messages.reviewSuccess', {action: actionText}));
                return true;
            }

            message.error(t('popReview.messages.reviewFailed'));
            return false;
        } catch (error) {
            showError(error);
            return false;
        }
    };

    return {
        handleReview,
        loading
    };
};
